import { useEffect, useEffectEvent } from "react";

/**
 * Escape-to-close and click-anywhere-to-close for a popover or tooltip.
 *
 * While `isOpen`, any click that bubbles up to `document`, or an Escape key
 * press, calls `onClose`. The element that opens the popover should call
 * `event.stopPropagation()` in its own click handler so the opening click
 * does not immediately close it. Replaces an `onClick` on a non-interactive
 * wrapper `<div>` (Sonar S6848 / S1082). For a modal backdrop use
 * `useModalDismiss` instead.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 */
export function useDismissOnClick(isOpen, onClose) {
    const close = useEffectEvent(() => onClose());

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                close();
            }
        };
        const handleClick = () => close();

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("click", handleClick);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("click", handleClick);
        };
    }, [isOpen]);
}
